/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { useSelect } from '../../../../../utils/selector.utils';
import { SelectInput } from '../../inputs/SelectInput';
import { FormTextInput } from '../../inputs/TextInput';
import { INodeFormProps } from './types';
import { INodeCreateFormNew, INodeUpdate, IOrganizationChartNode } from '../../../../../app/resource/node/node.types';
import { createNode, updateNode } from '../../../../../app/page/page-storage/node/node.actions';
import { DEFAULT_CHART_TYPE } from '../../../../../app/page/chart/chart.constants';
import { nodeSelectors } from '../../../../../app/resource/node/node.selectors';
import { ColorInput } from '../../inputs/ColorInput';
import { AvatarInput } from '../../inputs/AvatarInput';
import { IUser } from '../../../../../app/resource/user/user.types';
import { chartSelectors } from '../../../../../app/resource/chart/chart.selectors';
import {
  chartNodeOptionsWithDisambiguatedTitles,
  descendantIdsOfNode,
} from '../../../../../app/resource/node/node.utils';
import { computeCreateNodePlacement, externalUserFromForm, parentIdsFromForm } from './utils';

export const NodeForm: FunctionComponent<INodeFormProps> = ({ nodeData, parentId, chartType }) => {
  const dispatch = useDispatch();
  const formProps = useForm();
  const requiredValidation = useRequiredValidation();
  const users = useSelect(userSelectors.selectResourceList);
  const charts = useSelect(chartSelectors.selectResourceList);
  const draftChart = useSelect(chartSelectors.selectDraftChart);
  const activeChart = draftChart ?? charts.find((c) => c.type === DEFAULT_CHART_TYPE) ?? null;
  const allNodes = useSelect(nodeSelectors.selectResourceList);
  const chartNodes = useMemo(
    () => (activeChart ? allNodes.filter((n) => String(n.chartId) === String(activeChart.id)) : []),
    [activeChart, allNodes],
  );
  const nodesById = useMemo(() => new Map(chartNodes.map((n) => [String(n.id), n] as const)), [chartNodes]);
  const systemUsersById = useMemo(() => new Map(users.map((u) => [String(u.id), u] as const)), [users]);

  const defaultParent = useMemo(() => {
    const ids = nodeData ? nodeData.parentIds : parentId != null && String(parentId) !== '' ? [parentId] : [];

    if (ids.length === 0) {
      return undefined;
    }

    const optionsById = new Map(
      chartNodeOptionsWithDisambiguatedTitles(Array.from(nodesById.values()), systemUsersById).map((option) => [
        String(option.value),
        option,
      ]),
    );
    const options = ids
      .map((id) => optionsById.get(String(id)))
      .filter((x): x is { value: IOrganizationChartNode['id']; label: string } => x !== undefined);

    return options.length > 0 ? options : undefined;
  }, [nodeData, parentId, nodesById, systemUsersById]);

  const defaultUserType = useMemo(() => {
    if (!nodeData) {
      return undefined;
    }
    if (nodeData.userId) {
      return { value: 'system', label: 'System User' };
    }
    if (nodeData.externalUser) {
      return { value: 'custom', label: 'Custom User' };
    }
    return { value: 'vacant', label: 'Vacant' };
  }, [nodeData]);

  const systemUsersOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
    [users],
  );

  const excludedParentIds = useMemo(() => {
    if (!nodeData) {
      return new Set<string>();
    }
    const self = String(nodeData.id);
    const descendants = descendantIdsOfNode(self, chartNodes);
    return new Set<string>([self, ...Array.from(descendants)]);
  }, [nodeData, chartNodes]);

  const availableParents = useMemo(() => chartNodes.filter((node) => !excludedParentIds.has(String(node.id))), [
    chartNodes,
    excludedParentIds,
  ]);

  const parentOptions = useMemo(() => chartNodeOptionsWithDisambiguatedTitles(availableParents, systemUsersById), [
    availableParents,
    systemUsersById,
  ]);

  const userTypeSelection = formProps.watch('userTypeSelection');
  const userTypeOptions = [
    { value: 'system', label: 'System User' },
    { value: 'custom', label: 'Custom User' },
    { value: 'vacant', label: 'Vacant' },
  ];

  const defaultSystemUser = useMemo(() => {
    if (!nodeData || !nodeData.userId) {
      return undefined;
    }
    const user = systemUsersById.get(String(nodeData.userId));
    return user ? { value: user.id, label: `${user.firstName} ${user.lastName}` } : undefined;
  }, [nodeData, systemUsersById]);

  const onSubmit = (data: INodeCreateFormNew, nodeId?: IOrganizationChartNode['id']) => {
    const selection = data.userTypeSelection.value;
    const chart = chartType ?? DEFAULT_CHART_TYPE;
    const base = { description: data.description, color: data.color, nodeTitle: data.nodeTitle };
    const parentIds = parentIdsFromForm(data.parents);

    if (nodeId) {
      const newData: INodeUpdate = {
        id: nodeId,
        parentIds: parentIds.length ? parentIds : null,
        ...base,
      };
      if (selection === 'system') {
        newData.userId = data.user?.value ? data.user.value : undefined;
      } else if (selection === 'custom') {
        newData.externalUser = externalUserFromForm(data.externalUser);
      }
      dispatch(updateNode(nodeId, newData));
    } else {
      const placement = computeCreateNodePlacement(chartNodes, parentIds);
      const positionedBase = { ...base, parentIds, ...placement.createPosition };

      if (selection === 'system') {
        const user = systemUsersById.get(String(data.user?.value)) as IUser;
        dispatch(createNode({ ...positionedBase, userId: user.id }, chart));
      } else if (selection === 'custom') {
        dispatch(createNode({ ...positionedBase, externalUser: externalUserFromForm(data.externalUser) }, chart));
      } else {
        dispatch(createNode(positionedBase, chart));
      }
    }
  };

  return (
    <FormProvider {...formProps}>
      <form
        id="node-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit((formData) => onSubmit(formData as INodeCreateFormNew, nodeData?.id))}
        method="post"
      >
        <div className="field-container">
          <FormTextInput
            id="nodeTitle"
            name="nodeTitle"
            type="text"
            label="Seat Name"
            placeholder="Enter Seat Name"
            defaultValue={nodeData && nodeData.nodeTitle}
            validation={requiredValidation}
          />
        </div>
        <div className="field-container">
          <SelectInput
            id="userTypeSelection"
            name="userTypeSelection"
            label="User Type"
            placeholder="Chose..."
            options={userTypeOptions}
            defaultOptions={defaultUserType}
            // defaultValue={defaultChoseUser?.value}
            isSingle
            validation={requiredValidation}
          />
        </div>
        {userTypeSelection?.value === 'system' ? (
          <div className="field-container">
            <SelectInput
              id="user"
              name="user"
              label="User"
              placeholder="Search..."
              options={systemUsersOptions}
              defaultOptions={defaultSystemUser}
              isSingle
            />
          </div>
        ) : null}
        {userTypeSelection?.value === 'custom' && (
          <>
            <div className="popup-upload-panel">
              <AvatarInput
                id="image"
                name="externalUser.image"
                label={`Upload User Photo`}
                maxImageSize={2}
                acceptableFormats={'jpg, png, gif'}
                defaultValue={nodeData?.externalUser?.image}
              />
            </div>
            <div className="field-container">
              <FormTextInput
                id="firstName"
                name="externalUser.firstName"
                type="text"
                label="First Name"
                placeholder="Enter First Name"
                defaultValue={nodeData?.externalUser?.firstName ?? ''}
                validation={requiredValidation}
              />
            </div>
            <div className="field-container">
              <FormTextInput
                id="lastName"
                name="externalUser.lastName"
                type="text"
                label="Last Name"
                placeholder="Enter Last Name"
                defaultValue={nodeData?.externalUser?.lastName ?? ''}
              />
            </div>
          </>
        )}
        <div className="field-container">
          <FormTextInput
            id="description"
            name="description"
            type="text"
            label="Description"
            placeholder="Enter Description"
            defaultValue={nodeData ? nodeData.description : ''}
            multi
          />
        </div>
        <div className="field-container">
          <SelectInput
            id="parents"
            name="parents"
            label="Parent Seats"
            placeholder="Choose parent seats"
            options={parentOptions}
            defaultOptions={defaultParent}
          />
        </div>
        <div className="popup-upload-panel">
          <ColorInput id="color" name="color" placeholder="Pick Seat Color" defaultValue={nodeData?.color} />
        </div>
      </form>
    </FormProvider>
  );
};
